'use client'

// RegionOffers abstraction (Overmind→server-props migration, Bucket-A offers slice).
//
// WHAT THIS IS
// The dedicated seam that supplies the region SHOP OFFERS (`shopItems`) — the
// grid-projected, ResolvedImage-normalized offer array that drives the product
// gallery / price / buy-box on the four product-checkout forks — to the shared
// hooks/useProductOffers hook (and, through it, those forks), sourced from the
// SERVER-FETCHED region price bundle threaded as a prop, NOT from a render-time
// Overmind `state.shopItems` read. It mirrors components/NamedPrices,
// components/RegionContent and components/RegionSurfaces: a `'use client'` context
// Provider + a single accessor hook (`useRegionOffers()`).
//
// DATA SOURCE
// lib/regionPageData.loadRegionPageData already fetches the region price bundle
// (getRegionPriceBundle) and returns the grid-projected offers as `shopItems`
// (projectOffersForShopGrid(bundleShopItems, galleryFocusTypes), commit 3901257b —
// the SAME array threaded to the <Shop> grid island). That value is threaded into
// <RegionOffersProvider shopItems={...}> in the App Router page island
// (RegionPageClient), so it is available DURING the server SSR render and during
// the client's first render. It is the IDENTICAL array (same
// projectOffersForShopGrid call, same focusTypes) that buildRegionMutations
// hydrates into state.shopItems — so the provider value === the hydrated store
// value at SSR + first client render, which is what keeps the offer-driven render
// hydration-safe (SSR HTML === first client render, no React #418/#422). ZERO new
// fetch: this is a pure threading of already-loaded data, converging the shop-grid
// `shopItems` prop and the product-page offers onto ONE server-sourced path.
//
// THE POST-MOUNT FULL-OFFERS SWITCH (why this hook differs from its siblings)
// The server `shopItems` prop is the grid-CAPPED projection (galleries capped at 2,
// set/bundle image tail dropped — task #76). A product page needs the FULL offers,
// so useProductOffers fires a post-mount fetch (Phase D1: the Zustand
// store/offersStore.js `ensureFullOffers`, formerly the Overmind
// shopItemsRequiredPageRendered → getShopItems) that loads the complete offers into
// the offers store and sets `offersFull=true` (task #187). Therefore, unlike
// NamedPrices/RegionSurfaces (whose surfaces never change post-mount), this hook
// must NOT keep preferring the server prop forever — once the store's `full` flag
// is set, its FULL offers MUST win or the galleries stay capped (the #187 bug).
// Before that (SSR + first client render) the store is empty (full=false) so the
// render-time offers come from the byte-identical server prop, which is
// hydration-safe; after it, we defer to the upgraded store offers.
//
// APP-ROUTER SAFETY
// This module is a `'use client'` boundary (it holds React context + reads two
// client stores). It is imported ONLY by client components — so it introduces no
// new server-component/RSC coupling. No next/dynamic (the #130 landmine), no
// next/router, no window at render.
//
// SERVER-PROPS + OFFERS-STORE ONLY (Overmind retirement, Phase D2)
// The transitional Overmind grid fallback (state.shopItems) is GONE. The two live
// sources remain: the server-threaded grid prop (SSR + first render) and the
// Zustand offers store's FULL offers (post-mount). In production every offers
// surface either sits under a provider (server-prop grid) or triggers
// `ensureFullOffers` (chrome cart panel / restore-cart / product pages), so those
// two cover every live path; with neither, the hook returns an empty array.
//
// NOTE ON THE REFETCH GATE
// The full-offers fetch GATE lives with each consumer (useProductOffers, the shop
// fallback, SpecsPage, …) as a mount effect calling `ensureFullOffers` — the store
// itself is idempotent (#188 dedup + #187 full-vs-grid distinction), so the gate is
// structurally safe wherever it is called. This provider owns only the render-time
// offers DATA read.

import React, { createContext, useContext } from 'react'
import { useOffersStore, selectOffersFull, selectOffersFullFlag, selectOffersLoading } from '../../store/offersStore'

// Server-threaded grid-projected offers array, or null when no provider is present.
const RegionOffersContext = createContext(null)

export function RegionOffersProvider({ shopItems = null, children }) {
  // A stable value object per render is fine here: the provider sits at the page
  // island root (RegionPageClient), which re-renders rarely, and the consumer
  // reads a plain array (the memo-dep identity note below still holds).
  return (
    <RegionOffersContext.Provider value={{ shopItems }}>
      {children}
    </RegionOffersContext.Provider>
  )
}

/**
 * The single offers accessor for the product-checkout data hook and every other
 * offers reader converted off `state.shopItems` (Phase D1).
 *
 * Returns the effective offers array plus the length-derived `offersFetched`
 * flag, the authoritative `offersFull` flag, and the `offersLoading` fetch flag.
 *
 * Sourcing rule:
 *   - store.full   → the FULL post-mount offers (store/offersStore.offersFull) win.
 *   - otherwise    → the server-threaded grid-projected prop when present (SSR +
 *                    first client render, byte-identical to the grid the store will
 *                    replace); else an empty array (no provider / unit test).
 *
 * `offers` is a plain existing array reference (never a fresh copy), so it is
 * stable across renders within a phase — a caller may use it directly as a
 * useMemo dependency with the SAME recompute cadence as the old state.shopItems.
 *
 * Legacy aliases `shopItemsFetched`/`shopItemsFull` are kept so consumers not yet
 * renamed keep working; they equal `offersFetched`/`offersFull`.
 *
 * @returns {{ offers: any[], offersFetched: boolean, offersFull: boolean, offersLoading: boolean, shopItemsFetched: boolean, shopItemsFull: boolean }}
 */
export function useRegionOffers() {
  const ctx = useContext(RegionOffersContext)
  // Post-mount FULL offers now live on the Zustand offers store (Phase D1). These
  // are SSR-empty (full=false, offersFull=[]) so the server render + first client
  // render both take the server-prop grid branch → hydration-safe.
  const storeOffersFull = useOffersStore(selectOffersFull)
  const storeFull = useOffersStore(selectOffersFullFlag)
  const offersLoading = useOffersStore(selectOffersLoading)
  const serverShopItems = ctx ? ctx.shopItems : null
  const hasServerShopItems = Array.isArray(serverShopItems) && serverShopItems.length > 0

  const offers = storeFull
    ? (storeOffersFull || [])
    : hasServerShopItems
    ? serverShopItems
    : []

  const offersFetched = Array.isArray(offers) && offers.length > 0

  return {
    offers,
    offersFetched,
    offersFull: Boolean(storeFull),
    offersLoading: Boolean(offersLoading),
    // legacy aliases (pre-D1 names) — identical values.
    shopItemsFetched: offersFetched,
    shopItemsFull: Boolean(storeFull),
  }
}

export { RegionOffersContext }
